import {
  getTeamsSearchOptions,
  LineupEntry,
  postRostersByRosterIdPlayers,
  postRostersUnofficialUpload,
} from "@aura-frontend/omnibus-az-client";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogScrollableContent,
  DialogTitle,
  FileDropzone,
  Icons,
  isPopulatedArray,
  Label,
  pluralize,
  Select,
  toast,
  ValidationErrors,
} from "@win-reality/react-ui";
import { useState } from "react";
import { z } from "zod";

import { validateCsv } from "@/lib/csv";
import { downloadCSVTemplate } from "@/utils/csvUtils";
import {
  parseUploadedMembers,
  uploadedPlayerToRosterPlayerInfo,
} from "@/utils/uploadedPlayers";

const sideValues = [
  "left",
  "right",
  "switch",
  "Left",
  "Right",
  "Switch",
] as const;

const playerCsvSchema = z.object({
  first: z.string().min(1, "First name is required"),
  last: z.string().min(1, "Last name is required"),
  id: z.string().optional(),
  throws: z
    .enum(sideValues, { message: "Must be Left, Right, or Switch" })
    .optional(),
  bats: z
    .enum(sideValues, { message: "Must be Left, Right, or Switch" })
    .optional(),
  number: z.string().optional(),
});

export interface CreateRosterModalProps {
  orgId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: () => void;
  preSelectedRosterId?: string | null;
  onPlayersUploaded?: (externalIds: string[], players: LineupEntry[]) => void;
  /** Dialog theme (TaggingV2 follows its light/dark toggle) */
  variant?: "light" | "dark";
}

export default function UploadNewPlayersModal({
  orgId,
  open,
  onOpenChange,
  onSubmit,
  preSelectedRosterId,
  onPlayersUploaded,
  variant,
}: CreateRosterModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [selectedRosterId, setSelectedRosterId] = useState<string | undefined>(
    undefined,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const { data: teamsData } = useQuery({
    ...getTeamsSearchOptions({
      path: {
        org_id: encodeURIComponent(orgId),
      },
      query: {
        visibilityGroup: orgId,
      },
    }),
    select: (response) => response.teams,
    // Only the roster picker needs them
    enabled: preSelectedRosterId === undefined,
  });

  const rosterItems =
    teamsData?.map((team) => ({
      label: team.known_names?.find((name) => name) ?? team.id ?? "",
      value: team.id ?? "",
    })) ?? [];

  const onClear = () => {
    setFile(null);
    setSelectedRosterId(undefined);
    setValidationErrors([]);
  };

  const handleUpload = async () => {
    if (!file) return;

    setIsSubmitting(true);
    try {
      const uploadResponse = await postRostersUnofficialUpload({
        body: { file: [file] },
        query: {
          teamName: null!,
          orgId,
        },
      });

      if (uploadResponse && uploadResponse?.error) {
        const error = uploadResponse.error as { detail?: string };

        if (error.detail) {
          throw new Error(error.detail);
        }

        throw new Error("Error occurred while uploading the players");
      }

      const uploadedPlayers =
        uploadResponse.data?.team?.unofficial_members || [];

      const rosterIdToUse =
        preSelectedRosterId !== undefined
          ? preSelectedRosterId
          : selectedRosterId;

      if (rosterIdToUse && uploadedPlayers.length > 0) {
        // The upload already created the players: link them by id
        const playerInfo = parseUploadedMembers(uploadedPlayers).map(
          uploadedPlayerToRosterPlayerInfo,
        );

        if (playerInfo.length > 0) {
          await postRostersByRosterIdPlayers({
            body: { player_info: playerInfo },
            path: { roster_id: rosterIdToUse },
          });
        }
      }

      if (onPlayersUploaded && uploadedPlayers.length > 0) {
        const externalIds = uploadedPlayers
          .map((m: LineupEntry) => m.externalId)
          .filter((id): id is string => Boolean(id));

        onPlayersUploaded(externalIds, uploadedPlayers);
      }

      toast("Players uploaded successfully!", {
        variant: "success",
        duration: 6000,
      });
      onOpenChange(false);
      onSubmit();
      onClear();
    } catch (err: unknown) {
      console.error("Upload error:", err);
      const errorMessage =
        err instanceof Error ? err.message : "Please try again.";
      toast(`Something went wrong: ${errorMessage}`, {
        variant: "error",
        duration: 6000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onClear();
        }
        onOpenChange(isOpen);
      }}
      defaultCancelButton={false}
      size="xl"
      variant={variant}
    >
      {({ showDivider, setScrollContainer }) => (
        <>
          <DialogScrollableContent setScrollContainer={setScrollContainer}>
            <DialogHeader>
              <DialogTitle>Upload New Players</DialogTitle>

              <DialogDescription>
                To begin, download the spreadsheet and fill out your player
                information
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col">
              <div className="flex flex-col gap-4">
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                <div
                  onClick={downloadCSVTemplate}
                  className="shadow-glow-soft mt-3 mb-4 flex h-15 cursor-pointer items-center justify-between rounded px-5"
                >
                  <div className="flex items-center">
                    <img
                      src="/csv_icon.svg"
                      alt="CSV"
                      className="w-[2.063rem]"
                    />

                    <div className="text-sm ml-4">player_template.csv</div>
                  </div>

                  <div className="flex gap-2 text-sm items-center">
                    <Icons.CloudDownloadOutline className="h-6 w-6" />
                    Download
                  </div>
                </div>

                {preSelectedRosterId === undefined && (
                  <div>
                    <Label className="mb-2 text-sm">
                      Add to roster (optional)
                    </Label>
                    <Select
                      name="roster-select"
                      items={rosterItems}
                      value={selectedRosterId}
                      onValueChange={setSelectedRosterId}
                      placeholder="Select Roster"
                    />
                  </div>
                )}

                <FileDropzone
                  title="Upload spreadsheet, review details, and invite participants"
                  accept={{ "text/csv": [".csv"] }}
                  maxFiles={1}
                  files={file ? [file] : []}
                  maxSize={10 * 1024 * 1024}
                  instructions="Drag and drop a CSV file or click to browse"
                  onFilesAccepted={(accepted) => {
                    const selectedFile = accepted[0];
                    if (!selectedFile) return;

                    setValidationErrors([]);

                    validateCsv(selectedFile, playerCsvSchema)
                      .then((errors) => {
                        if (isPopulatedArray(errors)) {
                          const formatted = errors.map((err) => {
                            let message = `Row ${err.row}: ${err.message}`;
                            if (err.column !== "Structure") {
                              message += ` (${err.column})`;
                            }
                            if (err.value !== undefined && err.value !== "") {
                              message += ` - received: "${err.value}"`;
                            }
                            return message;
                          });
                          setValidationErrors(formatted);
                          return;
                        }

                        setFile(selectedFile);
                      })
                      .catch((error) => {
                        console.error(error);
                        toast("Failed to parse CSV file", {
                          variant: "error",
                        });
                      });
                  }}
                  onFilesRejected={(rejections) => {
                    toast(
                      `Rejected: ${rejections.map((r) => r.file.name).join(", ")}`,
                      { variant: "error" },
                    );
                  }}
                />

                {isPopulatedArray(validationErrors) && (
                  <div className="border-error mt-4 border-l-4 p-1 pl-3">
                    <div className="text-sm">
                      File Validation{" - "}
                      {pluralize(validationErrors.length, "Error", "Errors")}:
                    </div>
                    <ValidationErrors
                      errors={validationErrors}
                      typographyClassName="!text-md "
                    />
                  </div>
                )}
              </div>
            </div>
          </DialogScrollableContent>

          <DialogFooter showDivider={showDivider}>
            <div className="flex w-full justify-between">
              <Button
                variant="ghost"
                onClick={() => onOpenChange(false)}
                className="underline"
              >
                Cancel
              </Button>
              <Button
                variant="secondary-flat-light"
                isDisabled={!file}
                isLoading={isSubmitting}
                onClick={handleUpload}
              >
                Upload
              </Button>
            </div>
          </DialogFooter>
        </>
      )}
    </Dialog>
  );
}
